import { MutableRefObject, useEffect } from "react";
import { Editor, useEditor, useEditorState, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import "./OwRichTextEditor.scss";

type Props = {
    name: string;
    /**
     * The current value as an HTML string.
     */
    value?: string;
    /**
     * Change handler. Receives a synthetic event shaped like a native input
     * change ({ target: { name, value } }) so it drops into existing handlers
     * such as handleSupplierChange without modification.
     */
    fn: (e: { target: { name: string; value: string } }) => void;
    disabled?: boolean;
    /**
     * Receives the TipTap editor instance so a parent can drive it (e.g. insert a merge
     * variable at the caret). Null until the editor mounts and after it unmounts.
     */
    editorRef?: MutableRefObject<Editor | null>;
};

/**
 * OwRichTextEditor
 *
 * A WYSIWYG rich text editor (TipTap) that emits HTML. It mirrors the
 * value/fn/name contract of OwInput/OwTextArea so it can replace a plain text
 * input wherever HTML content is desired.
 */
const OwRichTextEditor = (props: Props) => {
    const editor = useEditor({
        extensions: [StarterKit],
        content: props.value || "",
        editable: !props.disabled,
        onUpdate: ({ editor }) => {
            // Normalize empty content to "" instead of TipTap's "<p></p>" so a
            // cleared message stores a clean empty value.
            const html = editor.isEmpty ? "" : editor.getHTML();
            props.fn({ target: { name: props.name, value: html } });
        },
    });

    // Sync external value changes (e.g. the Clear button setting value back to
    // empty) into the editor without re-emitting an update.
    useEffect(() => {
        if (!editor) {
            return;
        }

        const incoming = props.value || "";
        const isAlreadyEmpty = editor.isEmpty && incoming === "";

        if (incoming !== editor.getHTML() && !isAlreadyEmpty) {
            editor.commands.setContent(incoming, { emitUpdate: false });
        }
    }, [props.value, editor]);

    // Reflect disabled changes onto the editor.
    useEffect(() => {
        editor?.setEditable(!props.disabled);
    }, [props.disabled, editor]);

    // Expose the instance to the parent.
    useEffect(() => {
        if (props.editorRef) {
            props.editorRef.current = editor;
        }

        return () => {
            if (props.editorRef) {
                props.editorRef.current = null;
            }
        };
    }, [editor, props.editorRef]);

    const toolbarState = useEditorState({
        editor,
        selector: ({ editor }) => ({
            isBold: editor?.isActive("bold") ?? false,
            isItalic: editor?.isActive("italic") ?? false,
            isBulletList: editor?.isActive("bulletList") ?? false,
            isOrderedList: editor?.isActive("orderedList") ?? false,
            isLink: editor?.isActive("link") ?? false,
        }),
    });

    if (!editor) {
        return null;
    }

    const setLink = () => {
        const previousUrl = editor.getAttributes("link").href || "";
        const url = window.prompt("Enter URL", previousUrl);

        if (url === null) {
            return; // cancelled
        }

        if (url === "") {
            editor.chain().focus().extendMarkRange("link").unsetLink().run();
            return;
        }

        editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
    };

    const btnClass = (active: boolean) => "btn btn-sm me-1 mb-1 " + (active ? "btn-primary" : "btn-outline-secondary");

    return (
        <div className={"OwRichTextEditor"}>
            <div className={"OwRichTextEditor-toolbar"}>
                <button
                    type={"button"}
                    className={btnClass(toolbarState.isBold)}
                    title={"Bold"}
                    disabled={props.disabled}
                    onClick={() => editor.chain().focus().toggleBold().run()}>
                    <i className={"fa fa-bold"} />
                </button>
                <button
                    type={"button"}
                    className={btnClass(toolbarState.isItalic)}
                    title={"Italic"}
                    disabled={props.disabled}
                    onClick={() => editor.chain().focus().toggleItalic().run()}>
                    <i className={"fa fa-italic"} />
                </button>
                <button
                    type={"button"}
                    className={btnClass(toolbarState.isBulletList)}
                    title={"Bullet list"}
                    disabled={props.disabled}
                    onClick={() => editor.chain().focus().toggleBulletList().run()}>
                    <i className={"fa fa-list-ul"} />
                </button>
                <button
                    type={"button"}
                    className={btnClass(toolbarState.isOrderedList)}
                    title={"Numbered list"}
                    disabled={props.disabled}
                    onClick={() => editor.chain().focus().toggleOrderedList().run()}>
                    <i className={"fa fa-list-ol"} />
                </button>
                <button
                    type={"button"}
                    className={btnClass(toolbarState.isLink)}
                    title={"Link"}
                    disabled={props.disabled}
                    onClick={setLink}>
                    <i className={"fa fa-link"} />
                </button>
                <button
                    type={"button"}
                    className={"btn btn-sm btn-outline-secondary mb-1"}
                    title={"Clear formatting"}
                    disabled={props.disabled}
                    onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}>
                    <i className={"fa fa-remove-format"} />
                </button>
            </div>
            <EditorContent className={"OwRichTextEditor-content form-control"} editor={editor} />
        </div>
    );
};

export default OwRichTextEditor;
